import React, {useEffect, useRef} from 'react';
import {X} from '@phosphor-icons/react';

type Props = React.PropsWithChildren<{
  busy: boolean;
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLElement | null>;
  fallbackFocusRef: React.RefObject<HTMLElement | null>;
}>;

export default function AuthModal({children, busy, onClose, returnFocusRef, fallbackFocusRef}: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const backdropPress = useRef(false);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const previousOverflow = document.body.style.overflow;
    const landing = element.closest<HTMLElement>('.pp-landing');
    const previousLandingOverflow = landing?.style.overflow || '';
    if (landing) landing.style.overflow = 'hidden';
    element.showModal();
    document.body.style.overflow = 'hidden';
    element.querySelector<HTMLInputElement>('input:not([readonly])')?.focus();
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (landing) landing.style.overflow = previousLandingOverflow;
      const target = returnFocusRef.current || fallbackFocusRef.current;
      if (target?.isConnected) target.focus();
    };
  }, [returnFocusRef, fallbackFocusRef]);
  function isBackdrop(event: React.PointerEvent<HTMLDialogElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.target === event.currentTarget && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
  }
  return <dialog className="pp-login-modal" ref={dialog} aria-labelledby="landing-login-heading" aria-modal="true" aria-busy={busy}
    onCancel={event => {event.preventDefault(); if (!busy) onClose();}}
    onPointerDown={event => {backdropPress.current = isBackdrop(event);}}
    onPointerUp={event => {if (backdropPress.current && isBackdrop(event) && !busy) onClose(); backdropPress.current = false;}}
    onKeyDown={event => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button, a[href], input, [tabindex]'))
        .filter(element => !element.matches(':disabled, [tabindex="-1"]') && element.getClientRects().length > 0);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) {event.preventDefault(); return;}
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement as HTMLElement))) {event.preventDefault(); last?.focus();}
      else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement as HTMLElement))) {event.preventDefault(); first.focus();}
    }}>
    <button className="pp-login-modal-close" type="button" aria-label="Close sign in" aria-disabled={busy} onClick={() => {if (!busy) onClose();}}><X size={20}/></button>
    <div className="pp-auth-shell pp-login-modal-content"><div className="pp-auth-entry"><div className="pp-auth-entry-inner">
      <p className="pp-login-modal-brand">PITCHPILOT</p>{children}
    </div></div></div>
  </dialog>;
}
